import type React from 'react';
import { createElement } from 'react';
import { Typography } from '@mui/material';
import type { CoverageRule, CoveragePricing } from '@/shared/types';
import type { ScreeningResultOption } from '../types';
import { ScreeningResult } from '../constants';
import { parseMoney } from '@/utils/ParseMoney';
import { formatCoverageSubtitle } from '@/shared/utils/coverage-subtitle.util';
import { filterCollidingSmbRules } from '@/shared/utils/smb-coverage-dedupe.util';

export { calculateLeaseDurationMonths } from '@/shared/utils/leaseDurationMonths.util';
export { formatCoverageSubtitle } from '@/shared/utils/coverage-subtitle.util';

type SmbRequiredPricingParams = {
  leaseStartDate?: Date | string | null;
  leaseEndDate?: Date | string | null;
  grossMonthlyRent?: number | string | null;
};

/** Matches the pricing tuple `useDynamicCoverageRules` waits for before SMB fetches. */
export const hasSmbRequiredPricingParams = ({
  leaseStartDate,
  leaseEndDate,
  grossMonthlyRent,
}: SmbRequiredPricingParams): boolean =>
  Boolean(leaseStartDate && leaseEndDate && grossMonthlyRent);

export const coverageMonthCountOptions = [3, 6, 12] as const;

const SCREENING_RESULT_BY_RULE_ID: Record<string, ScreeningResult> = {
  approved: ScreeningResult.Approved,
  conditional: ScreeningResult.ApprovedWithConditions,
  denied: ScreeningResult.Denied,
};

export type SmbCoverageOption = {
  id: string;
  value: string;
  text: string;
  subText: string;
  screeningResult: ScreeningResult;
  pricing?: CoveragePricing;
  disabled?: boolean;
};

/**
 * Calculates the gross total coverage based on months of coverage and rent details.
 * Subtracts prepaid rent and free rent from the total.
 */
export const calculateGrossTotalCoverage = (
  leaseGuaranteeMonthsOverride?: number,
  grossMonthlyRent?: number,
  freeRent?: number,
  prepaidRent?: number,
): number =>
  Math.max(
    0,
    (leaseGuaranteeMonthsOverride ?? 0) * (grossMonthlyRent ?? 0) -
      (prepaidRent ?? 0) -
      (freeRent ?? 0),
  );

/**
 * Calculates the number of months of coverage from total coverage and monthly rent.
 * Returns undefined if grossMonthlyRent is not a positive number.
 */
export const calculateMonthsOfCoverage = (
  grossMonthlyRent?: number,
  totalCoverage?: number,
): number | undefined =>
  grossMonthlyRent && grossMonthlyRent > 0
    ? Math.max(0, (totalCoverage ?? 0) / grossMonthlyRent)
    : undefined;

/**
 * Determines if a coverage option should be enabled based on lease duration.
 * - 3 months: enabled when lease >= 3 months
 * - 6 months: enabled when lease >= 6 months
 * - 12 months: enabled when lease >= 12 months
 */
export const isCoverageOptionEnabled = (
  coverageMonths: number,
  leaseDurationMonths: number,
): boolean => {
  if (coverageMonths === 3) {
    return leaseDurationMonths >= 3;
  }
  if (coverageMonths === 6) {
    return leaseDurationMonths >= 6;
  }
  if (coverageMonths === 12) {
    return leaseDurationMonths >= 12;
  }
  return false;
};

export const buildStaticSmbCoverageOptions = ({
  grossMonthlyRent,
  freeRent,
  prepaidRent,
  leaseDurationMonths,
}: {
  grossMonthlyRent?: number;
  freeRent?: number;
  prepaidRent?: number;
  leaseDurationMonths: number;
}): SmbCoverageOption[] =>
  coverageMonthCountOptions.map((monthCount, index) => {
    const screeningResults = [
      ScreeningResult.Approved,
      ScreeningResult.ApprovedWithConditions,
      ScreeningResult.Denied,
    ] as const;
    const grossCoverage = calculateGrossTotalCoverage(
      monthCount,
      grossMonthlyRent,
      freeRent,
      prepaidRent,
    );

    return {
      id: `coverage-${monthCount}-months`,
      value: String(monthCount),
      text: `${monthCount} months`,
      subText: grossMonthlyRent
        ? `${parseMoney(grossCoverage) || '$0'} max coverage`
        : '$0 max coverage',
      screeningResult: screeningResults[index],
      disabled: !isCoverageOptionEnabled(monthCount, leaseDurationMonths),
    };
  });

export const buildDynamicSmbCoverageOptions = (
  coverageRulesFromApi?: CoverageRule[],
): SmbCoverageOption[] =>
  filterCollidingSmbRules(coverageRulesFromApi ?? []).flatMap((rule) => {
    if (!rule.pricing) {
      return [];
    }

    const screeningResult = SCREENING_RESULT_BY_RULE_ID[rule.id];
    if (!screeningResult) {
      return [];
    }

    return [
      {
        id: rule.id,
        value: String(rule.pricing.total_months),
        text: `${rule.pricing.total_months} months`,
        subText: `${parseMoney(rule.pricing.total_amount) || '$0'} max coverage`,
        screeningResult,
        pricing: rule.pricing,
      },
    ];
  });

export const hasCustomCoverageRuleLabels = (coverageRules?: CoverageRule[] | null): boolean =>
  (coverageRules ?? []).some((rule) => rule.is_custom_label);

export const findSmbCoverageOption = (
  options: SmbCoverageOption[],
  selectedValue?: number | string,
  selectedId?: string,
): SmbCoverageOption | undefined => {
  // Prefer id (set by the user's click) over value, since rules can share a value.
  if (selectedId) {
    const byId = options.find((option) => option.id === selectedId);
    if (byId) {
      return byId;
    }
  }

  if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
    return undefined;
  }

  return options.find((option) => option.value === String(selectedValue));
};

export const resolveDynamicCoverageOptions = (
  staticOptions: ScreeningResultOption[],
  coverageRulesFromApi?: CoverageRule[],
): ScreeningResultOption[] => {
  if (!coverageRulesFromApi?.length) {
    return staticOptions;
  }

  const ruleById = new Map(coverageRulesFromApi.map((rule) => [rule.id, rule]));

  return staticOptions.flatMap((option) => {
    const rule = ruleById.get(option.id);
    if (!rule) {
      return [];
    }
    return [
      {
        ...option,
        text: rule.label != null && rule.label !== '' ? rule.label : option.text,
        subText: formatCoverageSubtitle(rule),
        tooltip:
          rule.tooltip != null && rule.tooltip !== ''
            ? createElement(
                Typography as React.ElementType,
                { color: 'white', component: 'span', variant: 'body2' },
                rule.tooltip,
              )
            : option.tooltip,
      },
    ];
  });
};

export const buildEmptyOptions = (count: number): ScreeningResultOption[] => {
  return Array.from({ length: count }, (_, index) => ({
    id: `empty-${index}`,
    text: '--',
    value: `__empty_${index}__`,
    disabled: true,
    disabledTooltip: null,
  }));
};
